import {StyleSheet, css} from 'aphrodite';
import * as d3 from 'd3';
import React, {useEffect, useMemo, useState} from 'react';

import MonacoEditor from 'llm/monaco/MonacoEditor';
import {MonacoLanguage} from 'llm/monaco/MonacoTypes';
import MolstarViewer, {MolstarWrapperProps} from 'molbio/molstar/Viewer';
import {FONT_FAMILY_MONOSPACED, FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT, GRAY_LIGHTER, NEAR_BLACK} from 'ui-library/Colors';
import MarkdownContent from 'ui-library/MarkdownContent';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {CenteredSpinner} from 'ui-library/Spinner';

const DISPLAY_ROW_LIMIT = 1000;

const MOLSTAR_MIME_TYPE_TO_FORMAT: Record<string, MolstarWrapperProps['format']> = {
  'chemical/x-pdb': 'pdb',
  'chemical/x-pdbqt': 'pdbqt',
  'chemical/x-mmcif': 'mmcif',
};

interface FileViewerBodyProps {
  url: string;
  mimeType: string;
}

/** Hook to fetch text content from a URL. */
export function useFileContent(url: string): {
  content: string | null;
  isLoading: boolean;
  error: string | null;
} {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setContent(null);
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then((text: string) => {
        if (!cancelled) {
          setContent(text);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load file content.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return {content, isLoading, error};
}

/** Wrapper that handles the loading/error states from useFileContent. */
function TextContentLoader({
  url,
  children,
}: {
  url: string;
  children: (content: string) => JSX.Element;
}): JSX.Element {
  const {content, isLoading, error} = useFileContent(url);

  if (isLoading) {
    return <CenteredSpinner />;
  }
  if (error) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />;
  }

  return children(content ?? '');
}

function TextFileContent({url}: {url: string}): JSX.Element {
  return (
    <TextContentLoader url={url}>
      {(content) => (
        <textarea
          className={css(styles.textPreview)}
          value={content}
          readOnly={true}
          placeholder="Empty file"
        />
      )}
    </TextContentLoader>
  );
}

function MarkdownFileContent({url}: {url: string}): JSX.Element {
  return (
    <TextContentLoader url={url}>
      {(content) => (
        <div className={css(styles.markdownPreview)}>
          <MarkdownContent content={content} />
        </div>
      )}
    </TextContentLoader>
  );
}

function CodeFileContent({url, language}: {url: string; language: MonacoLanguage}): JSX.Element {
  return (
    <TextContentLoader url={url}>
      {(content) => (
        <MonacoEditor
          width="100%"
          height="100%"
          value={content}
          language={language}
          theme="vs"
          options={{readOnly: true, minimap: {enabled: false}}}
        />
      )}
    </TextContentLoader>
  );
}

function CSVFileContent({url}: {url: string}): JSX.Element {
  return <TextContentLoader url={url}>{(content) => <CSVTable content={content} />}</TextContentLoader>;
}

function CSVTable({content}: {content: string}): JSX.Element {
  const csvRows = useMemo(() => d3.csvParseRows(content), [content]);

  if (!csvRows || csvRows.length === 0) {
    return <InlineBanner bannerType={InlineBannerType.INFO} text="The CSV file is empty." />;
  }

  const headerRow = csvRows[0];
  const allDataRows = csvRows.slice(1);
  const totalRows = allDataRows.length;
  const displayedRows = allDataRows.slice(0, DISPLAY_ROW_LIMIT);
  const hasMoreRows = totalRows > DISPLAY_ROW_LIMIT;

  return (
    <div className={css(styles.tableWrapper)}>
      {hasMoreRows && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`Showing the first ${DISPLAY_ROW_LIMIT} rows out of ${totalRows}`}
          supportingInfo="Download to view the complete data."
        />
      )}
      <Table
        calculateInView={true}
        className={css(styles.table)}
        emptyTableText="No data"
        fitContent={true}
        frozenHeader={true}
        getRowKey={(rowIndex: number) => rowIndex}
        isCompact={true}
        numRows={displayedRows.length}
        overflowVisible={true}
        showBorder={false}
      >
        {headerRow.map((header, columnIndex) => (
          <Column
            key={columnIndex}
            header={header}
            renderCell={(rowIndex: number) => (
              <Cell className={css(styles.cell)}>{displayedRows[rowIndex]?.[columnIndex] || ''}</Cell>
            )}
          />
        ))}
      </Table>
    </div>
  );
}

function MolstarFileContent({url, mimeType}: {url: string; mimeType: string}): JSX.Element {
  const format = MOLSTAR_MIME_TYPE_TO_FORMAT[mimeType];
  return <MolstarViewer url={url} format={format} className={css(styles.molstarViewer)} />;
}

export function FileViewerBody({url, mimeType}: FileViewerBodyProps): JSX.Element {
  switch (mimeType) {
    case 'application/json':
    case 'text/plain':
      return <TextFileContent url={url} />;
    case 'text/x-python-script':
    case 'text/x-python':
      return <CodeFileContent url={url} language="python" />;
    case 'text/markdown':
      return <MarkdownFileContent url={url} />;
    case 'text/csv':
      return <CSVFileContent url={url} />;
    case 'image/jpeg':
    case 'image/png':
      return <img src={url} className={css(styles.image)} alt="File preview" />;
    case 'chemical/x-pdb':
    case 'chemical/x-pdbqt':
    case 'chemical/x-mmcif':
      return <MolstarFileContent url={url} mimeType={mimeType} />;
    default:
      return (
        <div className={css(styles.unsupported)}>
          <InlineBanner bannerType={InlineBannerType.INFO} text="Preview not available for this file type." />
        </div>
      );
  }
}

const styles = StyleSheet.create({
  textPreview: {
    ...FONT_PROXIMA_13,
    width: '100%',
    height: '100%',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    resize: 'none',
    padding: 12,
    fontFamily: FONT_FAMILY_MONOSPACED,
    backgroundColor: GRAY_LIGHTER,
    color: NEAR_BLACK,
    boxSizing: 'border-box',
  },
  markdownPreview: {
    ...FONT_PROXIMA_13,
    padding: 12,
  },
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    boxSizing: 'border-box',
  },
  table: {
    borderRadius: 0,
    margin: 0,
  },
  cell: {
    ...FONT_PROXIMA_13,
    height: 36,
  },
  image: {
    maxWidth: '100%',
    objectFit: 'contain',
  },
  molstarViewer: {
    width: '100%',
    height: '100%',
    position: 'relative',
    minWidth: 10,
    minHeight: 10,
  },
  unsupported: {
    padding: 16,
  },
});
